import { useState, useEffect, useCallback } from 'react';

import api from './api/client';
import { loadCredentials, clearCredentials } from './services/auth';
import useProducts from './hooks/useProducts';
import { useCart } from './hooks/useCart';
import useBarcode from './hooks/useBarcode';

import Login from './components/Login';
import SearchBar from './components/SearchBar';
import CategoryFilter from './components/CategoryFilter';
import ProductGrid from './components/ProductGrid';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import OrderConfirmation from './components/OrderConfirmation';
import CustomerSearch from './components/CustomerSearch';
import InvoicePrint from './components/InvoicePrint';
import ScanIndicator from './components/ScanIndicator';
import SalesReport from './components/SalesReport';
import CameraScanner from './components/CameraScanner';
import Settings from './components/Settings';
import QuickAddProduct from './components/QuickAddProduct';
import GiftCardCreate from './components/GiftCardCreate';
import StockAdjust from './components/StockAdjust';
import ProductCull from './components/ProductCull';
import ProductEdit from './components/ProductEdit';

import { printReceipt } from './services/receiptPrinter';
import logo from './assets/logo.png';

function populateConfig(data) {
  window.symplePOS = {
    currency: data.currency,
    currencySymbol: data.currency_symbol,
    currencyPosition: data.currency_position,
    decimals: data.decimals,
    thousandSep: data.thousand_sep,
    decimalSep: data.decimal_sep,
    taxRate: (data.tax_enabled && data.tax_rates && data.tax_rates.length > 0)
      ? data.tax_rates[0].rate
      : null,
  };
}

export default function App() {
  // ── Auth ────────────────────────────────────────────────────────
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // ── Config ──────────────────────────────────────────────────────
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // ── Disconnect handler (shared with 401 interceptor) ──────────
  const handleDisconnect = useCallback(() => {
    api.disconnect();
    clearCredentials();
    setIsAuthenticated(false);
    setConfig(null);
    setSettings(null);
    window.symplePOS = {};
  }, []);

  // Register 401 handler once
  useEffect(() => {
    api.onUnauthorized(handleDisconnect);
  }, [handleDisconnect]);

  // Auto-login from saved credentials on mount
  useEffect(() => {
    const creds = loadCredentials();
    if (creds) {
      api.configure(creds.storeUrl, creds.username, creds.appPassword);
      setConfigLoading(true);
      api.getConfig()
        .then((data) => {
          populateConfig(data);
          setConfig(data);
          if (data.settings) setSettings(data.settings);
          setIsAuthenticated(true);
        })
        .catch(() => {
          // Saved credentials are stale — show login
          api.disconnect();
          clearCredentials();
        })
        .finally(() => {
          setConfigLoading(false);
          setAuthChecked(true);
        });
    } else {
      setAuthChecked(true);
    }
  }, []);

  // ── Login callback ─────────────────────────────────────────────
  const handleLogin = useCallback((configData) => {
    populateConfig(configData);
    setConfig(configData);
    if (configData.settings) setSettings(configData.settings);
    setIsAuthenticated(true);
  }, []);

  // ── Products ────────────────────────────────────────────────────
  const {
    products, categories, loading: productsLoading, error: productsError,
    total, totalPages, page, setPage,
    search, setSearch, category, setCategory,
    fetchProducts,
    patchProduct,
  } = useProducts({ enabled: isAuthenticated });

  // ── Cart ────────────────────────────────────────────────────────
  const cart = useCart();

  // ── Customer ────────────────────────────────────────────────────
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);

  // ── Barcode ─────────────────────────────────────────────────────
  const { lastScan } = useBarcode({
    onProductScanned: (product) => cart.addItem(product),
    onScanError: () => {},
    enabled: isAuthenticated,
  });

  // ── Checkout flow ───────────────────────────────────────────────
  const [showCheckout, setShowCheckout] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  // ── Modals ──────────────────────────────────────────────────────
  const [showSalesReport, setShowSalesReport] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showQuickAddProduct, setShowQuickAddProduct] = useState(false);
  const [showGiftCardCreate, setShowGiftCardCreate] = useState(false);
  const [stockAdjustProduct, setStockAdjustProduct] = useState(null);
  const [showProductCull, setShowProductCull] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState({ done: 0, total: 0 });

  // ── Handlers ────────────────────────────────────────────────────
  const handleProductClick = useCallback((product, variation) => {
    if (variation) {
      cart.addItem({
        ...variation,
        name: `${product.name} - ${variation.attributes ? Object.values(variation.attributes).join(', ') : variation.name}`,
        image_url: product.image_url,
        variation_id: variation.id,
        product_id: product.id,
      });
    } else {
      cart.addItem(product);
    }
  }, [cart]);

  const handleCheckout = useCallback(() => {
    if (cart.items.length > 0) {
      setShowCheckout(true);
    }
  }, [cart.items.length]);

  const handleOrderComplete = useCallback((order) => {
    setShowCheckout(false);
    setCompletedOrder(order);
    cart.clearCart();
    setSelectedCustomer(null);
  }, [cart]);

  const handleNewSale = useCallback(() => {
    setCompletedOrder(null);
    setShowInvoice(false);
  }, []);

  const handlePrintInvoice = useCallback(() => {
    setShowInvoice(true);
  }, []);

  const handlePrintReceipt = useCallback(() => {
    if (completedOrder && config) {
      printReceipt(completedOrder, config.shop, settings);
    }
  }, [completedOrder, config, settings]);

  const handleCustomerSelect = useCallback((customer) => {
    setSelectedCustomer(customer);
    setShowCustomerSearch(false);
  }, []);

  const handleCameraScan = useCallback((code) => {
    setShowCameraScanner(false);
    api.getProductBySku(code).then((product) => {
      cart.addItem(product);
    }).catch(() => {});
  }, [cart]);

  const handleSaveSettings = useCallback((updated) => {
    setSettings(updated);
    setShowSettings(false);
    api.saveSettings(updated).catch(() => {});
  }, []);

  const handleProductCreated = useCallback((product) => {
    cart.addItem(product);
  }, [cart]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!productToDelete) return;
    setDeleting(true);
    try {
      await api.deleteProduct(productToDelete.id);
      setProductToDelete(null);
      fetchProducts(undefined, undefined, page);
    } catch {
      // silently reset — dialog stays open so user can retry
    } finally {
      setDeleting(false);
    }
  }, [productToDelete, fetchProducts, page]);

  const handleToggleProductSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleExitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleBatchDeleteConfirm = useCallback(async () => {
    const ids = [...selectedIds];
    setBatchDeleting(true);
    setBatchDeleteProgress({ done: 0, total: ids.length });
    const CONCURRENCY = 5;
    let done = 0;
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const chunk = ids.slice(i, i + CONCURRENCY);
      await Promise.allSettled(chunk.map((id) => api.deleteProduct(id)));
      done += chunk.length;
      setBatchDeleteProgress({ done, total: ids.length });
    }
    setSelectedIds(new Set());
    setShowBatchDeleteConfirm(false);
    setSelectMode(false);
    setBatchDeleting(false);
    fetchProducts(undefined, undefined, page);
  }, [selectedIds, fetchProducts, page]);

  // ── Loading / Auth check ───────────────────────────────────────
  if (!authChecked || configLoading) {
    return (
      <div className="symple-pos-wrap">
        <div className="fixed inset-0 flex items-center justify-center bg-pos-bg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pos-primary mx-auto"></div>
            <p className="mt-4 text-pos-muted-fg text-sm">Loading SymplePOS...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Login screen ───────────────────────────────────────────────
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // ── Main layout ─────────────────────────────────────────────────
  return (
    <div className="symple-pos-wrap">
      <div className="fixed inset-0 flex bg-pos-bg">
        {/* Left: Product browsing area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top bar */}
          <header className="flex-shrink-0 bg-white border-b border-pos-border px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                {/* SympleHub logo */}
                <img src={logo} alt="SymplePOS" className="h-8 shrink-0" />
                <h1 className="text-xl font-bold text-pos-muted-fg">
                  SymplePOS
                </h1>
              </div>
              <div className="flex items-center gap-1.5">
                {/* Scanner indicator */}
                <div
                  className={`p-2 rounded-lg transition-colors ${
                    lastScan ? 'text-pos-primary bg-pos-primary/10' : 'text-pos-muted-fg'
                  }`}
                  title="Barcode Scanner Ready"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4V1m0 22v-3M4 12H1m22 0h-3" />
                    <rect x="5" y="5" width="14" height="14" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="9" y1="9" x2="9" y2="15" strokeLinecap="round" />
                    <line x1="12" y1="9" x2="12" y2="15" strokeLinecap="round" />
                    <line x1="15" y1="9" x2="15" y2="15" strokeLinecap="round" />
                  </svg>
                </div>

                {/* Quick-add product button */}
                <button
                  onClick={() => setShowQuickAddProduct(true)}
                  className="p-2 text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted rounded-lg transition-colors"
                  title="Add Product"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </button>

                {/* Batch delete button (shown when items selected) */}
                {selectMode && selectedIds.size > 0 && (
                  <button
                    onClick={() => setShowBatchDeleteConfirm(true)}
                    className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete {selectedIds.size}
                  </button>
                )}

                {/* Select mode toggle */}
                <button
                  onClick={() => selectMode ? handleExitSelectMode() : setSelectMode(true)}
                  className={`p-2 rounded-lg transition-colors ${
                    selectMode
                      ? 'text-white bg-pos-primary'
                      : 'text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted'
                  }`}
                  title={selectMode ? 'Exit selection mode' : 'Select products to delete'}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                  </svg>
                </button>

                {/* Product cull button */}
                <button
                  onClick={() => setShowProductCull(true)}
                  className="p-2 text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted rounded-lg transition-colors"
                  title="Cull Products"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>

                {/* Gift card button */}
                <button
                  onClick={() => setShowGiftCardCreate(true)}
                  className="p-2 text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted rounded-lg transition-colors"
                  title="Create Gift Card"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </button>

                {/* Camera scanner button */}
                <button
                  onClick={() => setShowCameraScanner(true)}
                  className="p-2 text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted rounded-lg transition-colors"
                  title="Camera Scanner"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>

                {/* Sales report button */}
                <button
                  onClick={() => setShowSalesReport(true)}
                  className="p-2 text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted rounded-lg transition-colors"
                  title="Sales Report"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>

                {/* Settings button */}
                <button
                  onClick={() => setShowSettings(true)}
                  className="p-2 text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted rounded-lg transition-colors"
                  title="Settings"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>

                {/* Disconnect button */}
                <button
                  onClick={handleDisconnect}
                  className="p-2 text-pos-muted-fg hover:text-pos-danger hover:bg-red-50 rounded-lg transition-colors"
                  title="Disconnect"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Search bar */}
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

          </header>

          {/* Content area: category sidebar + product grid */}
          <div className="flex-1 flex min-h-0 overflow-hidden">
            {/* Category sidebar */}
            {categories.length > 0 && (
              <aside className="flex-shrink-0 w-44 bg-white border-r border-pos-border overflow-y-auto">
                <CategoryFilter
                  categories={categories}
                  selected={category}
                  onSelect={setCategory}
                />
              </aside>
            )}

          {/* Product grid */}
          <main className="flex-1 overflow-auto p-4">
            {productsError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {productsError}
              </div>
            )}
            <ProductGrid
              products={products}
              loading={productsLoading}
              onProductClick={handleProductClick}
              onStockAdjust={selectMode ? null : setStockAdjustProduct}
              onDelete={selectMode ? null : setProductToDelete}
              onEdit={selectMode ? null : setProductToEdit}
              selectMode={selectMode}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleProductSelect}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </main>
          </div>
        </div>

        {/* Right: Cart sidebar */}
        <Cart
          cart={cart}
          onCheckout={handleCheckout}
        />
      </div>

      {/* ── Modals ─────────────────────────────────────────────── */}

      {showCheckout && (
        <Checkout
          cart={cart}
          customer={selectedCustomer}
          paymentMethods={config?.payment_methods || [
            { id: 'cash', label: 'Cash' },
            { id: 'card', label: 'Card' },
            { id: 'bank_transfer', label: 'Bank Transfer' },
          ]}
          onComplete={handleOrderComplete}
          onClose={() => setShowCheckout(false)}
          onChangeCustomer={() => {
            setShowCustomerSearch(true);
          }}
        />
      )}

      {completedOrder && !showInvoice && (
        <OrderConfirmation
          order={completedOrder}
          onPrintInvoice={handlePrintInvoice}
          onPrintReceipt={handlePrintReceipt}
          onNewSale={handleNewSale}
          crmEnabled={Boolean(settings?.crm_url)}
        />
      )}

      {showInvoice && completedOrder && (
        <InvoicePrint
          order={completedOrder}
          shopInfo={config?.shop}
          onClose={() => setShowInvoice(false)}
          settings={settings}
        />
      )}

      {showCustomerSearch && (
        <CustomerSearch
          onSelect={handleCustomerSelect}
          onClose={() => setShowCustomerSearch(false)}
          crmEnabled={Boolean(settings?.crm_url)}
        />
      )}

      {showSalesReport && (
        <SalesReport onClose={() => setShowSalesReport(false)} />
      )}

      {showCameraScanner && (
        <CameraScanner
          onScan={handleCameraScan}
          onClose={() => setShowCameraScanner(false)}
        />
      )}

      {showSettings && (
        <Settings
          onClose={() => setShowSettings(false)}
          settings={settings}
          onSave={handleSaveSettings}
        />
      )}

      {showQuickAddProduct && (
        <QuickAddProduct
          onClose={() => setShowQuickAddProduct(false)}
          onProductCreated={handleProductCreated}
          categories={categories}
        />
      )}

      {showGiftCardCreate && (
        <GiftCardCreate onClose={() => setShowGiftCardCreate(false)} />
      )}

      {stockAdjustProduct && (
        <StockAdjust
          product={stockAdjustProduct}
          onClose={() => setStockAdjustProduct(null)}
          onUpdated={(result) => patchProduct(result.id, result)}
        />
      )}

      {showProductCull && (
        <ProductCull onClose={() => { setShowProductCull(false); fetchProducts(); }} />
      )}

      {productToEdit && (
        <ProductEdit
          product={productToEdit}
          onClose={() => setProductToEdit(null)}
          onUpdated={(updated) => {
            patchProduct(updated.id, updated);
            setProductToEdit(null);
          }}
        />
      )}

      {productToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
            {deleting ? (
              <div className="flex items-center justify-center gap-3 py-2">
                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-red-600" />
                <p className="text-sm text-gray-600">Deleting…</p>
              </div>
            ) : (
              <>
                <h3 className="text-base font-bold text-gray-900">Delete product?</h3>
                <p className="mt-1 text-sm text-gray-500 line-clamp-2">{productToDelete.name}</p>
                <p className="mt-2 text-sm text-gray-500">It will be moved to the WordPress bin and can be recovered from WooCommerce if needed.</p>
                <div className="mt-5 flex gap-3">
                  <button
                    onClick={() => setProductToDelete(null)}
                    className="flex-1 rounded-lg border border-pos-border px-4 py-2 text-sm font-medium text-pos-foreground transition-colors hover:bg-pos-muted"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showBatchDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
            {batchDeleting ? (
              <div className="text-center py-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">
                  Deleting {batchDeleteProgress.done} / {batchDeleteProgress.total}…
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-base font-bold text-gray-900">Delete {selectedIds.size} products?</h3>
                <p className="mt-2 text-sm text-gray-500">They'll be moved to the WordPress bin and can be recovered from WooCommerce if needed.</p>
                <div className="mt-5 flex gap-3">
                  <button
                    onClick={() => setShowBatchDeleteConfirm(false)}
                    className="flex-1 rounded-lg border border-pos-border px-4 py-2 text-sm font-medium text-pos-foreground transition-colors hover:bg-pos-muted"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBatchDeleteConfirm}
                    className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                  >
                    Delete {selectedIds.size}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ScanIndicator lastScan={lastScan} />
    </div>
  );
}
